import React from 'react';
import type { RoutineDifficulty } from '../types/routine';
import { DIFFICULTY_LABELS, ROUTINE_DIFFICULTIES } from '../types/routine';

interface RoutineDifficultySelectorProps {
  value: RoutineDifficulty;
  onChange: (difficulty: RoutineDifficulty) => void;
  disabled?: boolean;
}

/**
 * Selector de nivel de la rutina diaria.
 *
 * Los tres niveles comparten los mismos ejercicios: lo que cambia es el
 * volumen (series, descanso, duración). Se avisa en la UI para que nadie
 * espere ejercicios distintos al cambiar de nivel.
 */
const RoutineDifficultySelector: React.FC<RoutineDifficultySelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => (
  <div className="bg-gray-800 rounded-lg p-3 sm:p-4 lg:p-6 mb-4 sm:mb-6">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
      <div className="flex-1 min-w-0">
        <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
          Nivel de la rutina
        </h3>
        <p className="text-gray-400 text-xs sm:text-sm">
          Mismos ejercicios; cambian las series, el descanso y la duración
        </p>
      </div>

      <div
        role="radiogroup"
        aria-label="Nivel de la rutina"
        className="inline-flex rounded-lg border border-gray-600 bg-gray-700 p-1 self-start sm:self-auto"
      >
        {ROUTINE_DIFFICULTIES.map((difficulty) => {
          const selected = difficulty === value;
          return (
            <button
              key={difficulty}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(difficulty)}
              className={[
                'px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium',
                'rounded-md transition-colors outline-none',
                'focus:ring-2 focus:ring-[#1f9e3f]',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                selected
                  ? 'bg-[#1f9e3f] text-white shadow-sm'
                  : 'text-gray-300 hover:text-white',
              ].join(' ')}
            >
              {DIFFICULTY_LABELS[difficulty]}
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

export default RoutineDifficultySelector;
